/**
 * overrideMap — the ONE canonical shape of a CMS slot-override map.
 *
 * Three different shapes exist in this codebase and they used to disagree,
 * which meant a runtime slot override was silently dropped:
 *
 *   public API   FLAT    { "home::x": "A" }                 (one locale)
 *   build snapshot NESTED { "home::x": { zh-Hant, zh-Hans, en } }
 *   tCms         NESTED  (read as cmsOverrideMap[k]?.[locale])
 *
 * The runtime fetch replaced the whole snapshot with the flat payload, so
 * every lookup missed. `normalizeOverrideMap` converts the flat payload to
 * the nested shape and MERGES it into the base instead of replacing it, so
 * the other locales keep their build-time values.
 *
 * This module is deliberately dependency-free (no i18n, no React) so both
 * the browser bundle and the Node-side prerender script can import it.
 */

export type OverrideLocale = 'zh-Hant' | 'zh-Hans' | 'en'

/** One slot's value, per locale. A locale is absent when it has no override. */
export type OverrideEntry = Partial<Record<OverrideLocale, unknown>>

/** `${pageSlug}::${slotKey}` → per-locale values. */
export type OverrideMap = Record<string, OverrideEntry>

export const OVERRIDE_KEY_SEP = '::'

export function makeOverrideKey(pageSlug: string, slotKey: string): string {
  return `${pageSlug}${OVERRIDE_KEY_SEP}${slotKey}`
}

/**
 * Convert the flat single-locale public payload into the nested per-locale
 * shape, merged over `base` (the build-time snapshot).
 *
 * `flat` values are placed at `locale` only — the other locales keep
 * whatever `base` had, so a zh-Hant runtime fetch never wipes the English
 * overrides that came from the snapshot.
 */
export function normalizeOverrideMap(
  flat: Record<string, unknown> | undefined | null,
  locale: string,
  base?: OverrideMap,
): OverrideMap {
  const out: OverrideMap = {}
  for (const [k, v] of Object.entries(base ?? {})) {
    out[k] = { ...v }
  }
  for (const [k, v] of Object.entries(flat ?? {})) {
    const entry: OverrideEntry = { ...(out[k] ?? {}) }
    entry[locale as OverrideLocale] = v
    out[k] = entry
  }
  return out
}

/** Read one slot's value for one locale, or undefined when not overridden. */
export function readOverride(
  map: OverrideMap | undefined,
  pageSlug: string,
  slotKey: string,
  locale: string,
): unknown {
  if (!map) return undefined
  const entry = map[makeOverrideKey(pageSlug, slotKey)]
  if (!entry) return undefined
  return entry[locale as OverrideLocale]
}

/** Has an override been stored for this key + locale? */
export function hasOverrideValue(
  map: OverrideMap | undefined,
  pageSlug: string,
  slotKey: string,
  locale: string,
): boolean {
  const v = readOverride(map, pageSlug, slotKey, locale)
  if (v === undefined || v === null) return false
  if (typeof v === 'string') return v.length > 0
  return true
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/**
 * Merge a CMS delta over an i18n default.
 *
 * The delta semantics are what make the CMS safe for structured content:
 *  - object → recursive merge; a missing key in the delta INHERITS the default
 *  - array  → index-wise merge; the resulting array keeps the DEFAULT length,
 *             so the rendered layout cannot change shape
 *  - scalar → the delta wins
 */
export function mergeOverrideDelta(base: unknown, delta: unknown): unknown {
  if (delta === undefined) return base
  if (Array.isArray(base) && Array.isArray(delta)) {
    return base.map((b, i) => (i < delta.length ? mergeOverrideDelta(b, delta[i]) : b))
  }
  if (isPlainObject(base) && isPlainObject(delta)) {
    const out: Record<string, unknown> = { ...base }
    for (const [k, v] of Object.entries(delta)) {
      out[k] = k in base ? mergeOverrideDelta(base[k], v) : v
    }
    return out
  }
  return delta
}
